import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, Calendar } from 'lucide-react';
import { DayEvent } from '@/types/billing-calendar';

interface RescheduleEventModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: {
    client_id: number;
    billing_rule_id?: number;
    original_day: number;
    rescheduled_day: number;
    year: number;
    month: number;
    reason?: string;
  }) => Promise<boolean>;
  event: DayEvent | null;
  clientId: number;
  year: number;
  month: number;
  blockedDays?: number[];
}

const RescheduleEventModal: React.FC<RescheduleEventModalProps> = ({
  open,
  onClose,
  onConfirm,
  event,
  clientId,
  year,
  month,
  blockedDays = []
}) => {
  const [rescheduledDay, setRescheduledDay] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const monthName = monthNames[month - 1];
  const lastDayOfMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const currentDay = today.getDate();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;

  useEffect(() => {
    if (event && open) {
      setRescheduledDay('');
      setReason('');
      setError(null);
    }
  }, [event, open]);

  const handleSubmit = async () => {
    if (!event) return;

    const day = typeof rescheduledDay === 'number' ? rescheduledDay : parseInt(String(rescheduledDay));

    // Validações
    if (!day || isNaN(day)) {
      setError('Selecione um dia válido');
      return;
    }

    if (day < 1 || day > lastDayOfMonth) {
      setError(`Dia deve estar entre 1 e ${lastDayOfMonth}`);
      return;
    }

    if (day === event.original_day) {
      setError('O novo dia deve ser diferente do dia original');
      return;
    }

    // Validar que não está reagendando para o passado
    if (isCurrentMonth && day < currentDay) {
      setError('Não é possível reagendar para um dia no passado');
      return;
    }

    // Validar que o dia não está bloqueado
    if (blockedDays.includes(day)) {
      setError(`Dia ${day} está bloqueado. Não é possível reagendar para este dia.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await onConfirm({
        client_id: clientId,
        billing_rule_id: event.id,
        original_day: event.original_day,
        rescheduled_day: day,
        year,
        month,
        reason: reason || undefined
      });

      if (success) {
        setRescheduledDay('');
        setReason('');
        onClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao reagendar evento');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setRescheduledDay('');
      setReason('');
      setError(null);
      onClose();
    }
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Reagendar Evento
          </DialogTitle>
          <DialogDescription>
            Mova temporariamente este evento para outro dia do mês. O reagendamento é válido apenas para {monthName} {year}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="text-sm space-y-1">
              <div>
                <span className="font-medium">Evento:</span> {event.event}
              </div>
              <div>
                <span className="font-medium">Dia original:</span> Dia {event.original_day}
              </div>
              <div>
                <span className="font-medium">Vencimento:</span> Dia {event.due_day}
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="rescheduledDay">
              Novo dia (1-{lastDayOfMonth})
            </Label>
            <Input
              id="rescheduledDay"
              type="number"
              min={isCurrentMonth ? currentDay : 1}
              max={lastDayOfMonth}
              value={rescheduledDay}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setRescheduledDay('');
                } else {
                  const num = parseInt(value);
                  if (!isNaN(num) && num >= 1 && num <= lastDayOfMonth) {
                    setRescheduledDay(num);
                    setError(null);
                  }
                }
              }}
              className="mt-1"
              placeholder={`Digite um dia entre ${isCurrentMonth ? currentDay : 1} e ${lastDayOfMonth}`}
            />
            {rescheduledDay && (
              <p className="text-sm text-gray-600 mt-1">
                Evento do dia {event.original_day} será executado no dia {rescheduledDay}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="reason">Motivo (opcional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Feriado, evento especial, etc."
              className="mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !rescheduledDay}
          >
            {loading ? 'Reagendando...' : 'Confirmar Reagendamento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleEventModal;

